import React, { useState, useEffect } from "react";
import { useBookmarks } from "../../hooks/useBookmarks";
import { supabase } from "../../services/supabase";
import BusinessCard from "../../components/ui/cards/BusinessCard";
import { Bookmark, Loader, Store } from "lucide-react";
import { BusinessWithDistance } from "../../components/ui/cards/types";

interface BookmarkedShop {
  id: string;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  contact_phone?: string;
  contact_email?: string;
  logo_url?: string;
  services?: string[] | string;
  business_hours?: any;
  rating?: number;
  coordinates?: { lat: number; lng: number };
  verified?: boolean;
  distance_km?: number;
}


const BookmarksPage: React.FC = () => {
  const { bookmarks, loading: bookmarksLoading, getBookmarksByType, removeBookmark } = useBookmarks();
  const [businesses, setBusinesses] = useState<BookmarkedShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bookmarks.length > 0) {
      fetchBookmarkedData();
    } else if (!bookmarksLoading) {
      setLoading(false);
    }
  }, [bookmarks, bookmarksLoading]);

  const fetchBookmarkedData = async () => {
    try {
      setLoading(true);
      const shopBookmarks = getBookmarksByType('shop');
      getBookmarksByType('deal');
      
      // Fetch bookmarked businesses
      if (shopBookmarks.length > 0) {
        const businessIds = shopBookmarks.map(b => b.business_id).filter(Boolean);
        if (businessIds.length > 0) {
          const { data: businessData, error: businessError } = await supabase
            .from('businesses')
            .select('*')
            .in('id', businessIds)
            .eq('business_status', 'active');

          if (businessError) {
            console.error('Error fetching businesses:', businessError);
          } else {
            setBusinesses(businessData || []);
          }
        }
      }

      // For now, we'll focus on shops only since deals require complex data structure
      // In the future, deals functionality can be added with proper type mapping
    } catch (err) {
      console.error('Error in fetchBookmarkedData:', err);
      setError('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  if (loading || bookmarksLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading your bookmarks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const totalBookmarks = businesses.length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="bg-red-100 p-3 rounded-full">
            <Bookmark className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Bookmarks</h1>
            <p className="text-gray-600">
              Your saved shops ({totalBookmarks} items)
            </p>
          </div>
        </div>

        {totalBookmarks === 0 ? (
          <div className="text-center py-12">
            <Bookmark className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookmarks yet</h3>
            <p className="text-gray-600 mb-6">
              Start exploring and bookmark your favorite shops to see them here.
            </p>
            <a
              href="/business/fitment-centres"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Store className="h-4 w-4 mr-2" />
              Browse Fitment Centres
            </a>
          </div>
        ) : (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Store className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Bookmarked Shops ({businesses.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => {
                // Find the bookmark for this business
                const shopBookmark = getBookmarksByType('shop').find(
                  (bm) => bm.business_id === business.id
                );
                // Transform business data to match BusinessWithDistance interface
                const businessWithDistance: BusinessWithDistance = {
                  id: business.id,
                  name: business.name,
                  description: business.description ?? null,
                  address: business.address ?? null,
                  city: business.city ?? null,
                  province: business.province ?? null,
                  contact_phone: business.contact_phone ?? null,
                  contact_email: business.contact_email ?? null,
                  logo_url: business.logo_url ?? null,
                  services: Array.isArray(business.services) ? business.services : business.services ? [business.services] : [],
                  business_hours: business.business_hours ?? null,
                  rating: business.rating,
                  review_count: (business as any).review_count,
                  distance_km: business.distance_km,
                  verified: business.verified ?? false,
                  slug: business.id,
                  owner_id: (business as any).owner_id || '',
                  location: (business as any).location || null,
                  service_radius: (business as any).service_radius || 0,
                  created_at: (business as any).created_at || new Date().toISOString(),
                  published: true,
                  is_active: (business as any).is_active ?? true,
                  rejection_reason: (business as any).rejection_reason ?? "",
                  gallery: (business as any).gallery ?? [],
                  website: (business as any).website ?? "",
                  business_status: (business as any).business_status ?? "active",
                  phone: business.contact_phone ?? "",
                  email: business.contact_email ?? "",
                  coordinates: business.coordinates ?? { lat: 0, lng: 0 },
                  openingHours: business.business_hours ?? {},
                  specialties: Array.isArray((business as any).specialties) ? (business as any).specialties : [],
                  reviews: Array.isArray((business as any).reviews) ? (business as any).reviews : [],
                  updated_at: (business as any).updated_at || new Date().toISOString(),
                };
                return (
                  <div key={business.id} className="relative">
                    <BusinessCard
                      business={businessWithDistance}
                      compact={true}
                      onViewProfile={() => {
                        window.location.href = `/business/${businessWithDistance.slug}`;
                      }}
                    />
                    {shopBookmark && (
                      <button
                        className="absolute top-2 right-2 z-30 px-2 py-1 bg-red-600 text-white rounded text-xs shadow hover:bg-red-700 transition"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await removeBookmark(shopBookmark.id);
                        }}
                        title="Remove from favorites"
                        aria-label="Remove from favorites"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookmarksPage;